import { FeatureAppMode } from '..';
import { fetchCarImage } from '../fetch-data/fetch-car-image';
import { fetchServiceConfig } from '../fetch-data/fetch-services-config';
import { fetchVehicleInformation } from '../fetch-data/fetch-vehicle-information';
import { FormAttributeModel } from '../hooks-store/typings/incentive-store';
import {
  MaintenanceSchedule,
  OwnersLiteratureDocumentsData,
} from '../typings/general';

export const validateMileage = (value: string) => {
  if (value == null) return false;

  const str = String(value).trim().toLowerCase();

  // Allow:
  // - 123456 / 123,456 / 123 456
  // - 3.5k / 350k / 350 k
  // - 2.75k miles
  // - optional "mi" / "miles"
  const mileageRegex =
    /^\s*(\d{1,3}(?:[,\s]?\d{3})*|\d+(?:\.\d+)?)(?:\s*k)?(?:\s*(?:mi|miles))?\s*$/i;
  if (!mileageRegex.test(str)) return false;

  // Strip unit suffix first
  const noUnit = str.replace(/\s*(mi|miles)\s*$/i, '').trim();

  // Detect trailing 'k' whether or not there's a space: '370k' or '370 k'
  const hasK = /k\s*$/i.test(noUnit);

  // Remove the 'k' if present, then normalize separators
  const numericText = noUnit.replace(/k\s*$/i, '').replace(/[,\s]/g, '');
  const parsed = parseFloat(numericText);
  if (!Number.isFinite(parsed)) return false;

  const miles = hasK ? parsed * 1000 : parsed;

  return miles >= 0 && miles <= 360000;
};

export const validateFormInput = (
  form: FormAttributeModel,
  mode: FeatureAppMode,
) => {
  const isMileageValid =
    mode === 'maintenance' && form.mileage
      ? validateMileage(form.mileage)
      : true;
  let isModelValid = true;
  let isModelYearValid = true;
  let isTrimValid = true;
  let isVinValid = true;

  if (form.selectedTab === 'modelDetails') {
    isModelValid = !!form.model;
    isModelYearValid = !!form.modelYear;
    isTrimValid = !!form.trim;
  } else {
    isVinValid = !!form.vin;
  }

  return {
    mileage: isMileageValid,
    vin: isVinValid,
    model: isModelValid,
    modelYear: isModelYearValid,
    trim: isTrimValid,
  };
};

export const parseMileageNumber = (value: number | string | undefined) => {
  if (!value) return 0;

  if (typeof value !== 'string') value = String(value);

  value = value.replace(/,/g, '').trim().toLowerCase();
  const match = value.match(/^(\d+(?:\.\d+)?)(k)?/);

  if (!match) return null;

  let number = parseFloat(match[1]);

  if (match[2] === 'k') {
    number *= 1000;
  }

  return number;
};

export const getModelCode = (value?: string) => {
  if (!value) return undefined;
  return value.slice(0, 3);
};

export const splitDocumentsByType = (
  maintenanceScheduleData: OwnersLiteratureDocumentsData[],
) => {
  const warrantyDocuments: OwnersLiteratureDocumentsData[] = [];
  const additionalResourcesDocuments: OwnersLiteratureDocumentsData[] = [];

  const additionalResourcesRegex: RegExp = /roadside|collision/i;
  const maintenanceCardRegex: RegExp = /maintenance\s*card/i;

  // Create maps to track the latest document version for each type
  const latestDocumentsMap = new Map<string, OwnersLiteratureDocumentsData>();

  maintenanceScheduleData.forEach((doc: OwnersLiteratureDocumentsData) => {
    if (doc.document_type) {
      const docKey = doc.document_type.toLowerCase();

      // Handle additional resources documents
      if (additionalResourcesRegex.test(doc.document_type)) {
        // Check if it's an additional resource document and update accordingly
        if (latestDocumentsMap.has(docKey)) {
          const existingDoc = latestDocumentsMap.get(docKey);
          if (existingDoc) {
            if (doc.edition > existingDoc.edition) {
              latestDocumentsMap.set(docKey, doc);
            }
          }
        } else {
          latestDocumentsMap.set(docKey, doc);
        }
      }
      // For warranty-related documents, excluding maintenance cards
      else if (!maintenanceCardRegex.test(doc.document_type)) {
        if (latestDocumentsMap.has(docKey)) {
          const existingDoc = latestDocumentsMap.get(docKey);
          if (existingDoc) {
            if (doc.edition > existingDoc.edition) {
              latestDocumentsMap.set(docKey, doc);
            }
          }
        } else {
          latestDocumentsMap.set(docKey, doc);
        }
      }
    }
  });

  // Separate documents into their respective arrays
  latestDocumentsMap.forEach((doc, key) => {
    if (additionalResourcesRegex.test(key)) {
      additionalResourcesDocuments.push(doc);
    } else {
      warrantyDocuments.push(doc);
    }
  });

  // Sort warrantyDocuments based on priority
  warrantyDocuments.sort((a, b) => {
    const getDocumentPriority = (docType: string): number => {
      if (
        /^warranty$/i.test(docType) ||
        /^vehicle\s*warranty$/i.test(docType)
      ) {
        return 0;
      }
      if (/tire\s*warranty/i.test(docType)) {
        return 1;
      }
      if (/ca\s*emissions/i.test(docType)) {
        return 2;
      }
      return 3;
    };

    const priorityA = a.document_type
      ? getDocumentPriority(a.document_type)
      : 999;
    const priorityB = b.document_type
      ? getDocumentPriority(b.document_type)
      : 999;

    return priorityA - priorityB;
  });

  return {
    warrantyDocuments,
    additionalResourcesDocuments,
  };
};

/**
 * Format a number with commas as thousands separators
 * @param number | string number - The number to format
 * @returns string Formatted number string with commas
 */
export function formatNumberWithCommas(number: number | string) {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Calculate service progress based on current mileage with fixed 10,000 mile intervals
 * @param number currentMileage - Current mileage of the vehicle
 * @param number serviceInterval - Service interval in miles (default: 10000)
 * @returns object Object containing progress value and service information
 */
export const calculateServiceProgress = (
  currentMileage: number | string,
  serviceInterval: number = 10000,
) => {
  const mileage = Number(currentMileage);

  if (mileage === 0) {
    return {
      progress: 0,
      nextServiceMileage: formatNumberWithCommas(serviceInterval),
      previousServiceMileage: '0',
      milesUntilNextService: formatNumberWithCommas(serviceInterval),
      isOverdue: false,
    };
  }

  // Calculate next service due point
  const nextServiceDue = Math.ceil(mileage / serviceInterval) * serviceInterval;

  // Calculate previous service point
  const previousServiceMileage = nextServiceDue - serviceInterval;

  // Check if service is overdue (current mileage >= next service due point)
  const isOverdue = mileage >= nextServiceDue;

  // Calculate miles until next service (can be 0 or negative if overdue)
  const milesUntilNextService = nextServiceDue - mileage;

  // Calculate progress (can exceed 1.0 if overdue)
  const progress = (mileage - previousServiceMileage) / serviceInterval;

  // Determine next service mileage (same as nextServiceDue)
  const nextServiceMileage = nextServiceDue;

  return {
    progress: isOverdue ? 1 : progress,
    nextServiceMileage: formatNumberWithCommas(nextServiceMileage),
    previousServiceMileage: formatNumberWithCommas(previousServiceMileage),
    milesUntilNextService: formatNumberWithCommas(
      Math.max(0, milesUntilNextService),
    ), // Never show negative values
    isOverdue,
  };
};

/**
 * Calculate service progress based on actual maintenance schedule data
 * @param currentMileage - Current mileage of the vehicle
 * @param maintenanceSchedule - Actual maintenance schedule from API
 * @returns object Object containing progress value and service information
 */
export const calculateServiceProgressFromSchedule = (
  currentMileage: number | string,
  maintenanceSchedule?: MaintenanceSchedule,
) => {
  const mileage = Number(currentMileage);

  if (!maintenanceSchedule?.packages) {
    return calculateServiceProgress(currentMileage);
  }

  const serviceIntervals: number[] = [];

  Object.keys(maintenanceSchedule.packages).forEach((intervalKey) => {
    const interval = parseInt(intervalKey.replace(/[^\d]/g, ''));
    if (!isNaN(interval)) {
      serviceIntervals.push(interval);
    }
  });

  serviceIntervals.sort((a, b) => a - b);

  if (serviceIntervals.length === 0) {
    return calculateServiceProgress(currentMileage);
  }

  let previousServiceMileage = 0;
  let nextServiceMileage = serviceIntervals[0];
  let isOverdue = false;

  for (let i = 0; i < serviceIntervals.length; i++) {
    const interval = serviceIntervals[i];

    if (mileage < interval) {
      nextServiceMileage = interval;
      previousServiceMileage = i > 0 ? serviceIntervals[i - 1] : 0;
      isOverdue = false;
      break;
    } else if (mileage === interval) {
      nextServiceMileage = interval;
      previousServiceMileage = i > 0 ? serviceIntervals[i - 1] : 0;
      isOverdue = true; 
      break;
    } else if (i === serviceIntervals.length - 1) {      
      previousServiceMileage = interval;
      nextServiceMileage = interval;
      isOverdue = true;
      break;
    }
  }

  if (isOverdue) {
    return {
      progress: 1,
      nextServiceMileage: formatNumberWithCommas(nextServiceMileage),
      previousServiceMileage: formatNumberWithCommas(previousServiceMileage),
      milesUntilNextService: '0',
      isOverdue: true,
    };
  }

  const intervalRange = nextServiceMileage - previousServiceMileage;
  const progress =
    intervalRange > 0 ? (mileage - previousServiceMileage) / intervalRange : 0;

  const milesUntilNextService = Math.max(0, nextServiceMileage - mileage);

  return {
    progress: Math.min(progress, 1),
    nextServiceMileage: formatNumberWithCommas(nextServiceMileage),
    previousServiceMileage: formatNumberWithCommas(previousServiceMileage),
    milesUntilNextService: formatNumberWithCommas(milesUntilNextService),
    isOverdue: false,
  };
};

export enum DocType {
  TireWarranty = 'tireWarranty',
  CAEmissions = 'caEmissions',
  Roadside = 'roadside',
  Warranty = 'warranty',
  Collision = 'collision',
  Unknown = 'unknown',
}

const DOC_TYPE_PATTERNS: Record<string, RegExp> = {
  [DocType.TireWarranty]: /tire\s*warranty/i,
  [DocType.CAEmissions]: /ca\s*emissions/i,
  [DocType.Roadside]: /roadside/i,
  [DocType.Warranty]: /warranty/i,
  [DocType.Collision]: /collision/i,
};

const TRANSLATION_KEYS = {
  titles: {
    [DocType.TireWarranty]: 'literature.downloadables.card.tireWarrantyTitle',
    [DocType.CAEmissions]:
      'literature.downloadables.card.californiaEmissionsTitle',
    [DocType.Roadside]: 'literature.downloadables.card.roadsideTitle',
    [DocType.Warranty]: 'literature.downloadables.card.maintenanceBookleTitle',
    [DocType.Collision]: 'literature.downloadables.card.collisionTitle',
    [DocType.Unknown]: 'no.key.found',
  },
  infoLists: {
    [DocType.TireWarranty]: {
      tireInfoKey1: 'literature.downloadables.card.tireWarrantyInfo1',
    },
    [DocType.CAEmissions]: {
      caEmissionsInfoKey1: 'literature.downloadables.card.californiaEmissions1',
      caEmissionsInfoKey2: 'literature.downloadables.card.californiaEmissions2',
      caEmissionsInfoKey3: 'literature.downloadables.card.californiaEmissions3',
    },
    [DocType.Roadside]: {
      roadsideInfoKey1: 'literature.downloadables.card.roadsideInfo1',
      roadsideInfoKey2: 'literature.downloadables.card.roadsideInfo2',
      roadsideInfoKey3: 'literature.downloadables.card.roadsideInfo3',
    },
    [DocType.Warranty]: {
      warrantyInfoKey1: 'literature.downloadables.card.maintenanceBookleInfo1',
      warrantyInfoKey2: 'literature.downloadables.card.maintenanceBookleInfo2',
      warrantyInfoKey3: 'literature.downloadables.card.maintenanceBookleInfo3',
    },
    [DocType.Collision]: {
      colissionInfoKey1: 'literature.downloadables.card.collisionInfo1',
      colissionInfoKey2: 'literature.downloadables.card.collisionInfo2',
      colissionInfoKey3: 'literature.downloadables.card.collisionInfo3',
    },
    [DocType.Unknown]: {
      key: 'no.key.found',
    },
  },
};

/**
 * Determine document type from string description
 */
export const detectDocumentType = (docType: string | undefined): DocType => {
  if (!docType) return DocType.Unknown;

  // Special case for tire warranty vs general warranty
  if (DOC_TYPE_PATTERNS[DocType.TireWarranty].test(docType)) {
    return DocType.TireWarranty;
  }

  // Check other document types
  for (const [type, pattern] of Object.entries(DOC_TYPE_PATTERNS)) {
    if (type !== DocType.Warranty && pattern.test(docType)) {
      return type as DocType;
    }
  }

  // Warranty is special case to ensure it doesn't match tire warranty
  if (
    DOC_TYPE_PATTERNS[DocType.Warranty].test(docType) &&
    !DOC_TYPE_PATTERNS[DocType.TireWarranty].test(docType)
  ) {
    return DocType.Warranty;
  }

  return DocType.Unknown;
};

/**
 * Get title translation key
 */
export const getTitleTranslationKey = (
  docType: string,
): { [key: string]: string } => {
  const type = detectDocumentType(docType);
  return { titleKey: TRANSLATION_KEYS.titles[type] };
};

/**
 * Get info list translation keys
 */
export const getInfoListkeys = (docType: string): { [key: string]: string } => {
  const type = detectDocumentType(docType);
  return TRANSLATION_KEYS.infoLists[type];
};

/**
 * Gets the image car from services config
 * @param servicesConfig
 * @param model
 * @param modelYear
 * @param updateModerlCardData
 */
export const getCarImageFromServiceConfig = (
  servicesConfig: any,
  model: string,
  modelYear: string,
  updateModerlCardData: (data: any) => void,
) => {
  // Normalize model name to match the keys in servicesConfig,
  let normalizedModelKey: string = model
    .toLowerCase() // Convert to lowercase
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/\./g, ''); // Remove periods (for ID.4 -> id4)

  // specal cases
  if (normalizedModelKey === 'gti') normalizedModelKey = 'golf-gti';
  else if (normalizedModelKey === 'id4') normalizedModelKey = 'id.4';
  else if (normalizedModelKey === 'idbuzz') normalizedModelKey = 'id.-buzz';

  let imageUrl: string | undefined;

  try {
    // look for model in servicesConfig
    if (servicesConfig[normalizedModelKey]) {
      imageUrl = servicesConfig[normalizedModelKey].modelYearImage[modelYear];
    }
  } catch (error) {
    console.error('Error finding image from servicesConfig:', error);
  }

  updateModerlCardData({
    carImage: imageUrl,
  });
};

const handleServiceConfigError = (error: Error) =>
  console.error('Error fetching service config:', error);

const handleVehicleInfoError = (error: Error) =>
  console.error('Error fetching vehicle information:', error);

const handleCarImageError = (error: Error) =>
  console.error('Error fetching car image:', error);

export const createAdditionalPromises = (
  isVinMode: boolean,
  faServicesUrl: string,
  apiConfig: string,
  vin?: string,
) => {
  const promises: any[] = [];

  if (!isVinMode) {
    promises.push(
      fetchServiceConfig({ faServicesUrl, apiKey: apiConfig }).catch(handleServiceConfigError),
    );
  } else if (isVinMode && vin) {
    promises.push(
      fetchVehicleInformation({ faServicesUrl, apiKey: apiConfig, vin }).catch(
        handleVehicleInfoError,
      ),
      fetchCarImage({ faServicesUrl, vin }).catch(handleCarImageError),
    );
  }

  return promises;
};
